<template>
  <div class="adform-root">
    <div v-if="show" class="adform-overlay" @click.self="handleClose">
      <div class="adform-modal">

        <div class="adform-head">
          <div class="adform-title">
            <fa-icon :icon="['fas', 'calendar-plus']" class="adform-icon" />
            Add Admin Date
          </div>
          <button class="adform-close" :disabled="saving" @click="handleClose">
            <fa-icon :icon="['fas', 'times']" />
          </button>
        </div>

        <form class="adform-form" @submit.prevent="submit">

          <!-- Title -->
          <div class="adform-field">
            <label class="adform-label">Title <span class="adform-req">*</span></label>
            <input
              v-model="form.title"
              class="adform-input"
              :class="{ 'is-error': errors.title }"
              type="text"
              maxlength="100"
              placeholder="e.g. Agong's Birthday"
              @input="errors.title = ''"
            />
            <span v-if="errors.title" class="adform-err">{{ errors.title }}</span>
          </div>

          <!-- Date -->
          <div class="adform-field">
            <date-input v-model="form.date" label="Date" :required="true" />
            <span v-if="errors.date" class="adform-err">{{ errors.date }}</span>
          </div>

          <!-- Phone + Email -->
          <div class="adform-row">
            <div class="adform-field">
              <label class="adform-label">Phone</label>
              <input v-model="form.phone" class="adform-input" type="text" maxlength="30" placeholder="+60 12-345 6789" />
            </div>
            <div class="adform-field">
              <label class="adform-label">Email</label>
              <input v-model="form.email" class="adform-input" type="email" maxlength="100" placeholder="contact@example.com" />
            </div>
          </div>

          <!-- Location + Time -->
          <div class="adform-row">
            <div class="adform-field">
              <label class="adform-label">Location</label>
              <input v-model="form.location" class="adform-input" type="text" maxlength="200" placeholder="e.g. KLCC, Kuala Lumpur" />
            </div>
            <div class="adform-field adform-field--time">
              <label class="adform-label">Time</label>
              <input v-model="form.time_val" class="adform-input" type="time" />
            </div>
          </div>

          <!-- Description -->
          <div class="adform-field">
            <label class="adform-label">Description</label>
            <textarea
              v-model="form.description"
              class="adform-textarea"
              maxlength="500"
              rows="3"
              placeholder="Optional notes or details…"
            ></textarea>
          </div>

          <div class="adform-foot">
            <app-button
              @click="submit"
              variant="custom"
              size="md"
              customBg="#d97706"
              customHoverBg="#B16105"
              style="flex:1"
            >
              <span><fa-icon :icon="['fas', saving ? 'spinner' : 'plus']" :spin="saving" /></span>
              <span>{{ saving ? 'Saving…' : 'Add Date' }}</span>
            </app-button>
            <app-button
              @click="handleClose"
              :disabled="saving"
              variant="custom"
              customBg="#9ca3af"
              customHoverBg="#6b7280"
              style="flex:1"
            >
              <span>Cancel</span>
            </app-button>
          </div>

        </form>
      </div>
    </div>

    <alert-modal :show="alert.show" :status="alert.status" :message="alert.message" @close="onAlertClose" />
  </div>
</template>

<script>
import DateInput from '@/components/form/DateInput.vue'
import AlertModal from '@/components/shared/AlertModal.vue'
import AppButton from './AppButton.vue'

export default {
  name: 'AdminDateForm',
  components: { DateInput, AlertModal, AppButton },
  props: {
    show: { type: Boolean, default: false },
    context: { type: String, default: 'tax' },
    year: { type: Number, default: () => new Date().getFullYear() }
  },
  data () {
    return {
      form: { title: '', date: '', phone: '', email: '', location: '', time_val: '', description: '' },
      errors: { title: '', date: '' },
      saving: false,
      alert: { show: false, status: 'success', message: '' }
    }
  },
  watch: {
    show (val) { if (val) this.reset() }
  },
  methods: {
    validate () {
      this.errors = { title: '', date: '' }
      let valid = true
      if (!this.form.title.trim()) {
        this.errors.title = 'Title is required.'
        valid = false
      }
      if (!this.form.date) {
        this.errors.date = 'Please select a date.'
        valid = false
      }
      return valid
    },
    async submit () {
      if (!this.validate()) return
      this.saving = true

      const [dd, mm, yyyy] = this.form.date.split('/')
      const day = parseInt(dd)
      const month = parseInt(mm)
      const year = parseInt(yyyy)

      try {
        const token = localStorage.getItem('adminToken')
        const res = await fetch('https://tyng-ca.com.my/api/admin/tasks/save-admin-date.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({
            context: this.context,
            title: this.form.title.trim(),
            day,
            month,
            year,
            phone: this.form.phone.trim(),
            email: this.form.email.trim(),
            location: this.form.location.trim(),
            time_val: this.form.time_val,
            description: this.form.description.trim()
          })
        })
        const data = await res.json()
        if (res.status === 401) {
          this.alert = { show: true, status: 'error', message: 'Session expired. Please refresh the page and try again.' }
          return
        }
        if (data && data.success) {
          this.$emit('saved', data.item)
          this.alert = { show: true, status: 'success', message: `Admin date "${data.item.title}" added.` }
        } else {
          this.alert = { show: true, status: 'error', message: data.message || 'Failed to save. Please try again.' }
        }
      } catch (err) {
        this.alert = { show: true, status: 'error', message: 'An unexpected error occurred.' }
      } finally {
        this.saving = false
      }
    },
    onAlertClose () {
      this.alert.show = false
      if (this.alert.status === 'success') {
        this.reset()
        this.$emit('close')
      }
    },
    reset () {
      this.form = { title: '', date: '', phone: '', email: '', location: '', time_val: '', description: '' }
      this.errors = { title: '', date: '' }
      this.saving = false
      this.alert = { show: false, status: 'success', message: '' }
    },
    handleClose () {
      if (this.saving) return
      this.reset()
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.adform-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.adform-modal {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.18);
  width: min(520px, 95vw);
  max-height: 90vh;
  overflow-y: auto;
}

.adform-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #e5e7eb;
}

.adform-title {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 8px;
}

.adform-icon {
  color: #d97706;
}

.adform-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  font-size: 1rem;
  padding: 4px;
  line-height: 1;
  transition: color 0.15s;
}
.adform-close:hover { color: #374151; }

.adform-form {
  padding: 18px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.adform-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.adform-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.adform-field--time {
  max-width: 160px;
}

.adform-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.adform-req {
  color: #ef4444;
  margin-left: 2px;
}

.adform-input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #111827;
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.adform-input:focus { border-color: #d97706; box-shadow: 0 0 0 2px rgba(217, 119, 6, 0.12); }
.adform-input.is-error { border-color: #ef4444; }

.adform-textarea {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #111827;
  background: #fff;
  outline: none;
  resize: vertical;
  min-height: 72px;
  transition: border-color 0.15s;
  box-sizing: border-box;
  font-family: inherit;
}
.adform-textarea:focus { border-color: #d97706; box-shadow: 0 0 0 2px rgba(217, 119, 6, 0.12); }

.adform-err {
  font-size: 0.75rem;
  color: #ef4444;
}

.adform-foot {
  display: flex;
  gap: 12px;
  margin-top: 4px;
}
</style>
